import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface PixverseV5T2vParams {
  /**
   * 正向提示词，必填
   */
  prompt: string;
  /**
   * 视频宽高比，可选，默认16:9
   * @default "16:9"
   * @values 16:9 | 9:16 | 1:1 | 4:3 | 3:4
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1' | '4:3' | '3:4';
  /**
   * 视频时长（秒），可选，5或10秒，默认5秒
   * @default 5
   * @values 5 | 10
   */
  duration?: 5 | 10;
  /**
   * 视频质量，可选，默认540p
   * @default "540p"
   * @values 270p | 540p | 720p | 1080p
   */
  quality?: '270p' | '540p' | '720p' | '1080p';
  /**
   * 负向提示词，可选
   */
  negative_prompt?: string;
  /**
   * 运动模式，可选，支持normal（正常）、fast（快速）
   * @values normal | fast
   */
  motion_mode?: 'normal' | 'fast';
  /**
   * 摄像机运动，可选
   * @values zoom_in | zoom_out | pan_left | pan_right | tilt_up | tilt_down
   */
  camera_movement?: 'zoom_in' | 'zoom_out' | 'pan_left' | 'pan_right' | 'tilt_up' | 'tilt_down';
  /**
   * 随机种子，可选，用于可重现的生成结果
   */
  seed?: number;
  /**
   * 模板ID，可选
   */
  template_id?: number;
}

export interface PixverseV5T2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v5_t2v
 *
 * PixVerse V5 文本到视频生成API
 * 
 * Model: v5
 * Features: 文本到视频生成功能
 * Duration: 5秒或10秒（默认5秒）
 * Aspect Ratio: 16:9、9:16、1:1、4:3、3:4（默认16:9）
 * Quality: 270p、540p、720p、1080p（默认540p）
 * Motion Mode: normal、fast
 * Camera Movement: zoom_in、zoom_out、pan_left、pan_right、tilt_up、tilt_down
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV5T2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV5T2v({
 *   // 参数见 PixverseV5T2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV5T2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV5T2v(
  params: PixverseV5T2vParams,
): Promise<PixverseV5T2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v5_t2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV5T2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
